import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import {
    Formik,
    Form,
    Field,
    FieldArray
} from "formik";
import { toast } from "react-toastify";

import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    makeStyles,
    TextField
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles(theme => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
    // o <form> fica entre o papel do diálogo e o conteúdo: precisa repassar o flex
    // para o conteúdo rolar e o rodapé ficar fixo
    form: { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 },
    formGrid: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: 16,
    },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    sectionTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: t.text,
        margin: "8px 0 0",
        paddingTop: 12,
        borderTop: `1px solid ${t.line}`,
    },
    help: { fontSize: 12, color: t.textMuted, fontWeight: 400, marginTop: 2 },
    options: {
        border: `1px solid ${t.line}`,
        borderRadius: t.radius.sm,
        overflow: "hidden",
    },
    optionRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: 12,
        "& + &": { borderTop: `1px solid ${t.line}` },
    },
    optionMain: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 },
    optionFile: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, minWidth: 0 },
    fileInput: { display: "none" },
    fileLabel: { display: "inline-flex", flex: "none" },
    fileName: {
        flex: 1,
        minWidth: 0,
        fontSize: 12.5,
        color: t.textSecondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    fileNameEmpty: { color: t.textMuted },
    removeButton: { color: t.semantic.error, marginTop: 4 },
    addButton: { alignSelf: "flex-start" },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: t.accent,
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}});

const FileListSchema = Yup.object().shape({
    name: Yup.string()
        .min(3, "nome muito curto")
        .required("Obrigatório"),
    message: Yup.string()
        .required("Obrigatório")
});

const FilesModal = ({ open, onClose, fileListId, reload }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);

    const [files, setFiles] = useState([]);
    const [selectedFileNames, setSelectedFileNames] = useState([]);


    const initialState = {
        name: "",
        message: "",
        options: [{ name: "", path: "", mediaType: "" }],
    };

    const [fileList, setFileList] = useState(initialState);

    useEffect(() => {
        try {
            (async () => {
                if (!fileListId) return;

                const { data } = await api.get(`/files/${fileListId}`);
                setFileList(data);
            })()
        } catch (err) {
            toastError(err);
        }
    }, [fileListId, open]);

    const handleClose = () => {
        setFileList(initialState);
        setFiles([]);
        onClose();
    };

    const handleSaveFileList = async (values) => {

        const uploadFiles = async (options, filesOptions, id) => {
            const formData = new FormData();
            formData.append("fileId", id);
            formData.append("typeArch", "fileList")
            filesOptions.forEach((fileOption, index) => {
                if (fileOption.file) {
                    formData.append("files", fileOption.file);
                    formData.append("mediaType", fileOption.file.type)
                    formData.append("name", options[index].name);
                    formData.append("id", options[index].id);
                }
            });

            try {
                const { data } = await api.post(`/files/uploadList/${id}`, formData);
                setFiles([]);
                return data;
            } catch (err) {
                toastError(err);
            }
            return null;
        }

        const fileData = { ...values, userId: user.id };

        try {
            if (fileListId) {
                const { data } = await api.put(`/files/${fileListId}`, fileData)
                if (data.options.length > 0)
                    // console.log(values.options)
                    uploadFiles(data.options, values.options, fileListId)
            } else {
                const { data } = await api.post("/files", fileData);
                if (data.options.length > 0)
                    uploadFiles(data.options, values.options, data.id)
            }
            toast.success(i18n.t("fileModal.success"));
            if (typeof reload == 'function') {
                reload();
            }
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                scroll="paper">
                <DialogTitle id="form-dialog-title">
                    {(fileListId ? `${i18n.t("fileModal.title.edit")}` : `${i18n.t("fileModal.title.add")}`)}
                </DialogTitle>
                <Formik
                    initialValues={fileList}
                    enableReinitialize={true}
                    validationSchema={FileListSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveFileList(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values }) => (
                        <Form className={classes.form}>
                            <DialogContent>
                                <div className={classes.formGrid}>
                                    <div className={classes.field}>
                                        <label className={classes.label} htmlFor="file-list-name">
                                            {i18n.t("fileModal.form.name")}
                                        </label>
                                        <Field
                                            as={TextField}
                                            id="file-list-name"
                                            name="name"
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                    </div>
                                    <div className={classes.field}>
                                        <label className={classes.label} htmlFor="file-list-message">
                                            {i18n.t("fileModal.form.message")}
                                        </label>
                                        <Field
                                            as={TextField}
                                            id="file-list-message"
                                            type="message"
                                            multiline
                                            minRows={4}
                                            fullWidth
                                            name="message"
                                            error={
                                                touched.message && Boolean(errors.message)
                                            }
                                            helperText={
                                                touched.message && errors.message
                                            }
                                            variant="outlined"
                                            size="small"
                                        />
                                    </div>
                                    <div className={classes.sectionTitle}>
                                        {i18n.t("fileModal.form.fileOptions")}
                                        <div className={classes.help}>
                                            Cada item envia um arquivo, com uma mensagem opcional.
                                        </div>
                                    </div>

                                    <FieldArray name="options">
                                        {({ push, remove }) => (
                                            <>
                                                {values.options && values.options.length > 0 && (
                                                    <div className={classes.options}>
                                                        {values.options.map((info, index) => {
                                                            const fileName = info.path ? info.path.split('/').pop() : selectedFileNames[index];
                                                            return (
                                                                <div
                                                                    className={classes.optionRow}
                                                                    key={`${index}-info`}
                                                                >
                                                                    <div className={classes.optionMain}>
                                                                        <Field
                                                                            as={TextField}
                                                                            placeholder={i18n.t("fileModal.form.extraName")}
                                                                            inputProps={{ "aria-label": i18n.t("fileModal.form.extraName") }}
                                                                            name={`options[${index}].name`}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            multiline
                                                                            fullWidth
                                                                            minRows={2}
                                                                        />
                                                                        <div className={classes.optionFile}>
                                                                            <input
                                                                                type="file"
                                                                                onChange={(e) => {
                                                                                    const selectedFile = e.target.files[0];
                                                                                    const updatedOptions = [...values.options];
                                                                                    updatedOptions[index].file = selectedFile;
                                                                                    setFiles('options', updatedOptions);

                                                                                    // Atualize a lista selectedFileNames para o campo específico
                                                                                    const updatedFileNames = [...selectedFileNames];
                                                                                    updatedFileNames[index] = selectedFile ? selectedFile.name : '';
                                                                                    setSelectedFileNames(updatedFileNames);
                                                                                }}
                                                                                className={classes.fileInput}
                                                                                name={`options[${index}].file`}
                                                                                id={`file-upload-${index}`}
                                                                            />
                                                                            <label htmlFor={`file-upload-${index}`} className={classes.fileLabel}>
                                                                                <Button
                                                                                    component="span"
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    startIcon={<AttachFileIcon />}
                                                                                >
                                                                                    {fileName ? "Trocar arquivo" : "Escolher arquivo"}
                                                                                </Button>
                                                                            </label>
                                                                            <span
                                                                                className={`${classes.fileName} ${fileName ? "" : classes.fileNameEmpty}`}
                                                                                title={fileName || undefined}
                                                                            >
                                                                                {fileName || "Nenhum arquivo selecionado"}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <Tooltip title="Remover item">
                                                                        <IconButton
                                                                            size="small"
                                                                            aria-label="Remover item"
                                                                            className={classes.removeButton}
                                                                            onClick={() => remove(index)}
                                                                        >
                                                                            <DeleteOutlineIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                <Button
                                                    className={classes.addButton}
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => {
                                                        push({ name: "", path: "" });
                                                        setSelectedFileNames([...selectedFileNames, ""]);
                                                    }}
                                                >
                                                    {i18n.t("fileModal.buttons.fileOptions")}
                                                </Button>
                                            </>
                                        )}
                                    </FieldArray>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleClose} disabled={isSubmitting}>
                                    {i18n.t("fileModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {fileListId
                                        ? `${i18n.t("fileModal.buttons.okEdit")}`
                                        : `${i18n.t("fileModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default FilesModal;
